import * as React from "react";
import styled from "styled-components";
import { PoseGroup } from "react-pose";
import MenuLayer from "../MenuLayer";
import Downshift, {
  StateChangeOptions,
  ControllerStateAndHelpers
} from "downshift";
import { useSelect } from "@hubper/theme";

import mergeStyles from "../../util/mergeStyles";
import highlightText from "../../util/highlightText";
import FilterInput from "../../elements/FilterInput";

import SelectMenu from "../../elements/SelectMenu";
import SelectMenuItem from "../../elements/SelectMenuItem";

const Wrapper = styled.div`
  display: inline-block;
  position: relative;
`;

type Item = { value: string | number; display: string };

type Props = {
  style?: React.CSSProperties;
  className?: string;
  value: string | null;
  api: (searchStr: string) => Promise<any>;
  responseToItems: (response: any) => Item[];
  onChange: (value: string | number) => void;
  stretch?: boolean;
  disabled?: boolean;
  error?: string | boolean;
  placeholder?: string;
  dashed?: boolean;
  resetOnSelect?: boolean;
};

type State = {
  items: Item[];
  isOpen: boolean;
  selectedItem: Item | null;
};

function SelectBox(
  {
    style,
    className,
    value,
    onChange,
    stretch,
    disabled,
    placeholder,
    api,
    dashed,
    responseToItems,
    resetOnSelect
  }: Props,
  ref: any
) {
  const [state, setState] = React.useState<State>({
    isOpen: false,
    items: [],
    selectedItem: null
  });

  const inputContainerRef = React.useRef<HTMLDivElement>(null);
  const { color } = useSelect();

  const handleStateChange = async (
    changes: StateChangeOptions<any>,
    stateAndHelpers: ControllerStateAndHelpers<any>
  ) => {
    if (changes.hasOwnProperty("selectedItem")) {
      setState({
        ...state,
        isOpen: false,
        selectedItem: resetOnSelect ? null : state.selectedItem,
        items: []
      });
      onChange(changes.selectedItem!.value);
      if (resetOnSelect) {
        stateAndHelpers.reset({
          inputValue: "",
          highlightedIndex: null,
          selectedItem: null
        });
      }
    } else if (changes.hasOwnProperty("inputValue")) {
      const inputValue = changes.inputValue!;
      if (inputValue && inputValue.length > 0) {
        const result = await api(inputValue);
        setState(state => ({
          ...state,
          items: responseToItems(result)
        }));
      } else {
        setState({
          ...state,
          isOpen: false,
          items: responseToItems([])
        });
      }
    }
    if (changes.isOpen !== undefined) {
      setState(state => ({
        ...state,
        isOpen: changes.isOpen!
      }));
    }
  };

  React.useEffect(() => {
    if (value === null || value === undefined) {
      setState({
        ...state,
        selectedItem: null
      });
    }
  }, [value]);

  return (
    <Downshift
      itemToString={item => (item && item.display) || undefined}
      selectedItem={value ? state.selectedItem : null}
      onStateChange={handleStateChange}
      isOpen={state.isOpen && state.items.length > 0}
      // inputValue={state.inputValue}
      // onInputValueChange={value => setState(s => ({ ...s, inputValue: value }))}
    >
      {({
        isOpen,
        getRootProps,
        getMenuProps,
        getItemProps,
        getInputProps,
        highlightedIndex,
        inputValue
      }) => {
        return (
          <Wrapper
            {...getRootProps()}
            style={mergeStyles({ width: stretch ? "100%" : 250 }, style)}
            className={className}
          >
            <FilterInput
              {...getInputProps({
                ref
              }) as any}
              containerRef={inputContainerRef}
              disabled={disabled}
              placeholder={placeholder}
              dashed={dashed}
              value={inputValue}
              style={{
                transition: "0.2s",
                borderBottomLeftRadius: isOpen ? 0 : 3,
                borderBottomRightRadius: isOpen ? 0 : 3,
                borderBottomColor: isOpen ? color(["grey", 1]) : undefined
              }}
            />

            <MenuLayer element={inputContainerRef.current}>
              {style => (
                <PoseGroup flipMove={false}>
                  {isOpen && (
                    <SelectMenu
                      dashed={dashed}
                      key="menu"
                      {...getMenuProps({
                        style
                      })}
                    >
                      {state.items.map((option, index) => {
                        const isSelected = option.value === value;

                        return (
                          <SelectMenuItem
                            selected={isSelected}
                            highlighted={highlightedIndex === index}
                            key={option.value}
                            {...getItemProps({
                              item: option,
                              index
                            })}
                          >
                            {inputValue
                              ? highlightText(
                                  option.display as string,
                                  inputValue
                                )
                              : option.display}
                          </SelectMenuItem>
                        );
                      })}
                    </SelectMenu>
                  )}
                </PoseGroup>
              )}
            </MenuLayer>
          </Wrapper>
        );
      }}
    </Downshift>
  );
}

export default React.forwardRef(SelectBox);
